// import { useState, useEffect, useCallback, useMemo } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   ArrowLeft, Search, Filter, X, CheckCircle, AlertCircle, ChevronDown,
//   Loader2, TrendingUp, RefreshCw, Download, ExternalLink,
//   XCircle, BarChart2, Database,
// } from "lucide-react";
// import { USER_API_END_POINT } from "@/utils/constant";
// import Footer from "../Footer";
// import Pagination from "./Pagination";

// /**
//  * ─────────────────────────────────────────────────────────────────────────
//  * WHY THIS LOOKS DIFFERENT FROM InternalDBPage UNDER THE HOOD
//  * ─────────────────────────────────────────────────────────────────────────
//  * InternalDBPage fetches every candidate once and filters/paginates in
//  * the browser. That's fine for a DB of a few thousand rows — it falls
//  * over at Ceipal's scale (300k+). So here, search/filters/page are sent
//  * to YOUR backend as query params, and only one page of results (10-50
//  * rows) ever reaches the browser. Your backend is also the only place
//  * that holds the Ceipal bearer token — see ceipalRoutes.example.js.
//  *
//  * Ceipal JSON -> UI field mapping (per the payload you shared):
//  *   name          <- first_name + middle_name + last_name
//  *   jobTitle      <- job_title
//  *   experience    <- experience
//  *   skills        <- skills / primary_skills (comma or array — normalize
//  *                    on the backend since Ceipal's exact delimiter isn't
//  *                    confirmed yet)
//  *   visaType      <- work_authorization
//  *   location      <- city, state, country
//  *   updatedAt     <- NOT in the sample payload — confirm the real field
//  *                    (likely something like modified_date / updated_on)
//  *   resumeUrl     <- NOT in the sample payload — Ceipal usually needs a
//  *                    separate "download resume" call per applicant_id;
//  *                    until that's wired up, we link out to the Ceipal
//  *                    profile instead of an inline preview.
//  * ─────────────────────────────────────────────────────────────────────────
//  */

// const TABS = [
//   { id: "search", label: "Search Ceipal", icon: Search },
//   { id: "analytics", label: "Analytics", icon: BarChart2 },
// ];

// const VISA_OPTS = [
//   { value: "all", label: "All Visa Types" },
//   { value: "Permanent_Resident", label: "Permanent Resident" },
//   { value: "Student_Visa", label: "Student Visa" },
//   { value: "Other", label: "Other" },
//   { value: "US_Citizen", label: "US Citizen" },
//   { value: "Employment Authorization Document", label: "Employment Authorization Document" },
//   { value: "Need_H1_Visa", label: "Need H-1 Visa" },
//   { value: "Have_H1_Visa", label: "Have H-1 Visa" },
//   { value: "TN_Permit_Holder", label: "TN Permit Holder" },
//   { value: "Unspecified", label: "Unspecified" },
// ];

// const EXP_OPTS = ["all", "0-2 yrs", "3-5 yrs", "6-10 yrs", "10+ yrs"];
// const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// function mapCeipalRecordToCandidate(item, index) {
//   const rawSkills = item.primary_skills || item.skills || "";
//   const skills = Array.isArray(rawSkills)
//     ? rawSkills
//     : String(rawSkills).split(",").map((s) => s.trim()).filter(Boolean);

//   return {
//     id: item.applicant_id || item.id || `ceipal-${index}`,
//     name: [item.first_name, item.middle_name, item.last_name].filter(Boolean).join(" ") || "Unknown",
//     email: item.email_address || null,
//     phone: item.mobile_number || item.work_phone_number || item.home_phone_number || null,
//     jobTitle: item.job_title || "",
//     experience: item.experience ? Number(item.experience) : null,
//     skills,
//     visaType: item.work_authorization || "Unspecified",
//     location: [item.city, item.state, item.country].filter(Boolean).join(", "),
//     linkedinUrl: item.linkedin_profile_url || null,
//     ceipalProfileUrl: item.profile_url || null, // confirm real field name with Ceipal
//     updatedAt: item.resume_updated_on || item.modified_date || null,
//   };
// }

// const expBucket = (years) => {
//   if (years == null) return null;
//   if (years <= 2) return "0-2 yrs";
//   if (years <= 5) return "3-5 yrs";
//   if (years <= 10) return "6-10 yrs";
//   return "10+ yrs";
// };

// const CeipalDBPage = () => {
//   const navigate = useNavigate();
//   const [activeTab, setActiveTab] = useState("search");

//   // Search & filter state — sent to backend, not applied client-side
//   const [searchInput, setSearchInput] = useState("");
//   const [searchQuery, setSearchQuery] = useState("");
//   const [visaFilter, setVisaFilter] = useState("all");
//   const [expFilter, setExpFilter] = useState("all");
//   const [showFilters, setShowFilters] = useState(false);

//   // Results
//   const [candidates, setCandidates] = useState([]);
//   const [totalCount, setTotalCount] = useState(0);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   // Pagination — server-side
//   const [currentPage, setCurrentPage] = useState(1);
//   const [pageSize, setPageSize] = useState(20);

//   // Row selection (current page only — selections don't persist across pages)
//   const [selectedIds, setSelectedIds] = useState(new Set());
//   const toggleSelectAll = () => {
//     setSelectedIds((prev) =>
//       prev.size === candidates.length ? new Set() : new Set(candidates.map((c) => c.id))
//     );
//   };
//   const toggleRow = (id) => {
//     setSelectedIds((prev) => {
//       const next = new Set(prev);
//       next.has(id) ? next.delete(id) : next.add(id);
//       return next;
//     });
//   };

//   // Analytics (separate aggregate call, not derived from `candidates`)
//   const [analytics, setAnalytics] = useState(null);
//   const [analyticsLoading, setAnalyticsLoading] = useState(false);
//   const [analyticsError, setAnalyticsError] = useState(null);

//   const fetchCandidates = useCallback(async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const params = new URLSearchParams({
//         page: currentPage,
//         pageSize,
//         ...(searchQuery ? { q: searchQuery } : {}),
//         ...(visaFilter !== "all" ? { visaStatus: visaFilter } : {}),
//         ...(expFilter !== "all" ? { expBucket: expFilter } : {}),
//       });

//       const res = await fetch(`${USER_API_END_POINT}/ceipal/candidates?${params}`, {
//         credentials: "include",
//       });
//       const data = await res.json();

//       if (!data.success) throw new Error(data.message || "Failed to load Ceipal candidates");

//       const list = Array.isArray(data.results) ? data.results : data.data || [];
//       setCandidates(list.map(mapCeipalRecordToCandidate));
//       setTotalCount(data.total ?? list.length);
//     } catch (err) {
//       console.error("Error fetching Ceipal candidates:", err);
//       setError(err.message || "Failed to load Ceipal candidates");
//     } finally {
//       setLoading(false);
//     }
//   }, [currentPage, pageSize, searchQuery, visaFilter, expFilter]);

//   useEffect(() => {
//     fetchCandidates();
//   }, [fetchCandidates]);

//   useEffect(() => {
//     setSelectedIds(new Set());
//   }, [currentPage, pageSize, searchQuery, visaFilter, expFilter]);

//   useEffect(() => {
//     setCurrentPage(1);
//   }, [searchQuery, visaFilter, expFilter]);

//   useEffect(() => {
//     if (activeTab !== "analytics") return;
//     const fetchAnalytics = async () => {
//       setAnalyticsLoading(true);
//       setAnalyticsError(null);
//       try {
//         const res = await fetch(`${USER_API_END_POINT}/ceipal/analytics`, { credentials: "include" });
//         const data = await res.json();
//         if (!data.success) throw new Error(data.message || "Failed to load analytics");
//         setAnalytics(data.analytics);
//       } catch (err) {
//         setAnalyticsError(err.message || "Failed to load analytics");
//       } finally {
//         setAnalyticsLoading(false);
//       }
//     };
//     fetchAnalytics();
//   }, [activeTab]);

//   const handleSearch = () => setSearchQuery(searchInput);
//   const clearSearch = () => {
//     setSearchInput("");
//     setSearchQuery("");
//   };

//   const activeFilterCount = [visaFilter !== "all", expFilter !== "all"].filter(Boolean).length;
//   const handleClearFilters = () => {
//     setVisaFilter("all");
//     setExpFilter("all");
//   };

//   const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
//   const handlePageChange = (page) => setCurrentPage(page);
//   const handlePageSizeChange = (size) => {
//     setPageSize(parseInt(size, 10));
//     setCurrentPage(1);
//   };

//   return (
//     <>
//       <div className="min-h-screen bg-[#f8f9fc]">
//         {/* ── Hero ──────────────────────────────────────────────────────── */}
//         <div className="relative w-full overflow-hidden">
//           <div className="absolute inset-0 bg-[#012760]" />
//           <div
//             className="absolute inset-0 opacity-[0.07]"
//             style={{
//               backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)",
//               backgroundSize: "28px 28px",
//             }}
//           />
//           <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-400 rounded-full opacity-10 blur-3xl" />
//           <div className="absolute -bottom-10 right-20 w-80 h-80 bg-blue-300 rounded-full opacity-10 blur-3xl" />
//           <div className="relative w-full px-4 md:px-8 lg:px-10 py-10 md:py-12">
//             <button
//               onClick={() => navigate(-1)}
//               className="flex items-center gap-2 text-white/50 hover:text-white text-xs font-medium mb-5 transition-colors"
//             >
//               <ArrowLeft className="w-3.5 h-3.5" />
//               Back to Candidates
//             </button>
//             <div className="flex items-start justify-between gap-4 flex-wrap">
//               <div>
//                 <div className="flex items-center gap-2 mb-2">
//                   <span className="text-[10px] font-bold uppercase tracking-widest text-blue-300">Hirees</span>
//                   <span className="w-1 h-1 rounded-full bg-blue-400 opacity-60" />
//                   <span className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Applicant Database</span>
//                 </div>
//                 <h1 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-2">
//                   Hirees <span className="text-blue-300">Candidate</span> Search
//                 </h1>
//                 <p className="text-white/50 text-sm max-w-xl">
//                   Search across your full Ceipal applicant database in real time — results are fetched
//                   page by page, so this stays fast even with 300,000+ resumes.
//                 </p>
//               </div>
//               <div className="flex gap-3 flex-wrap">
//                 <div className="flex items-center gap-2 bg-white/10 border border-white/10 rounded-xl px-4 py-2">
//                   <Database className="w-3.5 h-3.5 text-white/60" />
//                   <span className="text-white text-xs font-semibold">
//                     {totalCount ? totalCount.toLocaleString() : "—"} in Ceipal
//                   </span>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* ── Tab bar ───────────────────────────────────────────────────── */}
//         <div className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
//           <div className="w-full px-4 md:px-8 lg:px-10 flex gap-0">
//             {TABS.map(({ id, label, icon: Icon }) => (
//               <button
//                 key={id}
//                 onClick={() => setActiveTab(id)}
//                 className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap
//                   ${activeTab === id
//                     ? "border-[#012760] text-[#012760]"
//                     : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200"
//                   }`}
//               >
//                 <Icon className="w-3.5 h-3.5" />
//                 {label}
//               </button>
//             ))}
//           </div>
//         </div>

//         <div className="w-full px-4 md:px-8 lg:px-10 py-8">
//           {/* ══════════════════════ TAB: Search ══════════════════════ */}
//           {activeTab === "search" && (
//             <div>
//               <div className="flex gap-3 mb-4 flex-wrap">
//                 <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 shadow-sm focus-within:border-[#012760] transition-colors">
//                   <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
//                   <input
//                     type="text"
//                     placeholder="Search name, skills, job title…"
//                     className="w-full text-sm text-gray-700 placeholder-gray-400 bg-transparent outline-none"
//                     value={searchInput}
//                     onChange={(e) => setSearchInput(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                   {searchInput && (
//                     <button onClick={clearSearch}>
//                       <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
//                     </button>
//                   )}
//                 </div>

//                 <button
//                   onClick={handleSearch}
//                   className="px-5 py-2.5 bg-[#012760] text-white text-sm font-semibold rounded-xl hover:bg-[#023080] transition-colors shadow-sm"
//                 >
//                   Search
//                 </button>

//                 <button
//                   onClick={() => setShowFilters(!showFilters)}
//                   className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-all shadow-sm
//                     ${showFilters || activeFilterCount > 0
//                       ? "border-[#012760] text-[#012760] bg-[#012760]/5"
//                       : "border-gray-200 text-gray-600 bg-white hover:border-[#012760] hover:text-[#012760]"
//                     }`}
//                 >
//                   <Filter className="w-3.5 h-3.5" />
//                   Filters
//                   {activeFilterCount > 0 && (
//                     <span className="w-4 h-4 rounded-full bg-[#012760] text-white text-[9px] font-bold flex items-center justify-center">
//                       {activeFilterCount}
//                     </span>
//                   )}
//                   <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? "rotate-180" : ""}`} />
//                 </button>

//                 <button
//                   onClick={fetchCandidates}
//                   className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all"
//                 >
//                   <RefreshCw className="w-3.5 h-3.5" />
//                   Refresh
//                 </button>

//                 <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all">
//                   <Download className="w-3.5 h-3.5" />
//                   Export
//                 </button>
//               </div>

//               {showFilters && (
//                 <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-5 shadow-sm">
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
//                     <div>
//                       <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Visa Status</p>
//                       <select
//                         value={visaFilter}
//                         onChange={(e) => setVisaFilter(e.target.value)}
//                         className="w-full border border-gray-200 rounded-lg text-sm px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-[#012760]/20 focus:border-[#012760]"
//                       >
//                         {VISA_OPTS.map((v) => (
//                           <option key={v.value} value={v.value}>{v.label}</option>
//                         ))}
//                       </select>
//                     </div>
//                     <div>
//                       <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Experience</p>
//                       <select
//                         value={expFilter}
//                         onChange={(e) => setExpFilter(e.target.value)}
//                         className="w-full border border-gray-200 rounded-lg text-sm px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-[#012760]/20 focus:border-[#012760]"
//                       >
//                         {EXP_OPTS.map((v) => (
//                           <option key={v} value={v}>{v === "all" ? "Any Experience" : v}</option>
//                         ))}
//                       </select>
//                     </div>
//                   </div>
//                   {activeFilterCount > 0 && (
//                     <div className="mt-4 pt-4 border-t border-gray-100">
//                       <button
//                         onClick={handleClearFilters}
//                         className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 font-medium"
//                       >
//                         <XCircle className="w-3.5 h-3.5" />
//                         Clear all filters
//                       </button>
//                     </div>
//                   )}
//                 </div>
//               )}

//               <div className="flex items-center justify-between mb-4">
//                 <p className="text-sm font-semibold text-gray-700">
//                   {loading ? (
//                     <span className="flex items-center gap-2 text-gray-400">
//                       <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
//                     </span>
//                   ) : (
//                     <>
//                       {totalCount.toLocaleString()}{" "}
//                       <span className="font-normal text-gray-400">
//                         {totalCount === 1 ? "candidate" : "candidates"} in Hirees
//                       </span>
//                     </>
//                   )}
//                 </p>
//               </div>

//               {error && (
//                 <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-4 flex items-center gap-3">
//                   <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
//                   <p className="text-sm text-red-700">{error}</p>
//                   <button
//                     onClick={fetchCandidates}
//                     className="ml-auto text-xs font-medium text-red-600 hover:text-red-800 underline"
//                   >
//                     Retry
//                   </button>
//                 </div>
//               )}

//               {!loading && candidates.length === 0 && !error ? (
//                 <div className="bg-white rounded-2xl border border-gray-100 p-14 text-center shadow-sm">
//                   <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
//                     <Search className="w-5 h-5 text-gray-400" />
//                   </div>
//                   <p className="text-gray-700 font-semibold mb-1">No candidates found</p>
//                   <p className="text-gray-400 text-sm">Try adjusting your search or clearing filters</p>
//                 </div>
//               ) : (
//                 <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
//                   <div
//                     className="overflow-auto"
//                     style={{ maxHeight: "calc(100vh - 320px)" }}
//                   >
//                     <table className="min-w-full text-sm">
//                       <thead className="sticky top-0 z-10">
//                         <tr className="bg-[#eef1fb] border-b border-[#012760]/10">
//                           <th className="w-10 px-3 py-3">
//                             <input
//                               type="checkbox"
//                               checked={candidates.length > 0 && selectedIds.size === candidates.length}
//                               onChange={toggleSelectAll}
//                               className="rounded border-gray-300 accent-[#012760]"
//                             />
//                           </th>
//                           {[
//                              "NAME", "EMAIL ID", "CONTACT NUMBER", "JOB TITLE", "EXPERIENCE",
//                             "CURRENT EMPLOYER", "LOCATION", "WORK AUTHORIZATION",
//                              "NOTICE PERIOD", "RESUME UPDATED ON", "",
//                           ].map((label) => (
//                             <th
//                               key={label}
//                               className="px-3 py-3 text-left text-[11px] font-semibold text-[#012760] tracking-wide whitespace-nowrap"
//                             >
//                               {label}
//                             </th>
//                           ))}
//                         </tr>
//                       </thead>
//                       <tbody>
//                         {candidates.map((c) => (
//                           <tr
//                             key={c.id}
//                             className="border-b border-gray-50 hover:bg-[#012760]/5 transition-colors"
//                           >
//                             <td className="px-3 py-2.5">
//                               <input
//                                 type="checkbox"
//                                 checked={selectedIds.has(c.id)}
//                                 onChange={() => toggleRow(c.id)}
//                                 className="rounded border-gray-300 accent-[#012760]"
//                               />
//                             </td>
//                             <td className="px-3 py-2.5 text-[#012760] font-medium whitespace-nowrap">{c.name}</td>
//                             <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.email || "N/A"}</td>
//                             <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.phone || "N/A"}</td>
//                             <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap max-w-[160px] truncate" title={c.jobTitle}>
//                               {c.jobTitle || "N/A"}
//                             </td>
//                             <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
//                               {c.experience != null ? `${c.experience} yrs` : "N/A"}
//                             </td>
//                             {/* Not present in Ceipal payload — confirm field name once available */}
//                             <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">N/A</td>
//                             <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap max-w-[180px] truncate" title={c.location}>
//                               {c.location || "N/A"}
//                             </td>
//                             <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.visaType}</td>
                           
//                             {/* Not present in Ceipal payload — confirm field name once available */}
                           
//                             <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">N/A</td>
//                             <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.updatedAt || "N/A"}</td>
//                             <td className="px-3 py-2.5 whitespace-nowrap">
//                               {c.ceipalProfileUrl || c.linkedinUrl ? (
//                                 <a
//                                   href={c.ceipalProfileUrl || c.linkedinUrl}
//                                   target="_blank"
//                                   rel="noreferrer"
//                                   className="flex items-center gap-1.5 text-[11px] border rounded-lg px-2.5 py-1 font-medium text-[#012760] border-[#012760] hover:bg-[#012760] hover:text-white transition-all w-fit"
//                                 >
//                                   <ExternalLink className="w-3 h-3" /> View
//                                 </a>
//                               ) : (
//                                 <span className="text-[11px] text-gray-300">—</span>
//                               )}
//                             </td>
//                           </tr>
//                         ))}
//                       </tbody>
//                     </table>
//                   </div>
//                 </div>
//               )}

//               {totalCount > 0 && (
//                 <Pagination
//                   currentPage={currentPage}
//                   totalPages={totalPages}
//                   onPageChange={handlePageChange}
//                   pageSize={pageSize}
//                   totalItems={totalCount}
//                   onPageSizeChange={handlePageSizeChange}
//                 />
//               )}
//             </div>
//           )}

//           {/* ══════════════════════ TAB: Analytics ══════════════════════ */}
//           {activeTab === "analytics" && (
//             <div>
//               {analyticsLoading ? (
//                 <div className="flex items-center justify-center h-48 text-gray-400 gap-2">
//                   <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
//                 </div>
//               ) : analyticsError ? (
//                 <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-3">
//                   <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
//                   <p className="text-sm text-red-700">{analyticsError}</p>
//                 </div>
//               ) : (
//                 <>
//                   <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
//                     {[
//                       { label: "Total in Ceipal", value: analytics?.totalCount ?? "—", icon: Database, color: "text-[#012760]", bg: "bg-[#f0f4ff]" },
//                       { label: "Avg Experience", value: analytics?.avgExperience ? `${analytics.avgExperience}y` : "—", icon: TrendingUp, color: "text-purple-700", bg: "bg-purple-50" },
//                       { label: "US Citizens / GC", value: analytics?.citizenCount ?? "—", icon: CheckCircle, color: "text-green-700", bg: "bg-green-50" },
//                       { label: "Need Sponsorship", value: analytics?.sponsorshipCount ?? "—", icon: AlertCircle, color: "text-orange-600", bg: "bg-orange-50" },
//                     ].map(({ label, value, icon: Icon, color, bg }) => (
//                       <div key={label} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
//                         <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center mb-3`}>
//                           <Icon className={`w-4 h-4 ${color}`} />
//                         </div>
//                         <p className="text-2xl font-bold text-gray-800 mb-0.5">{value}</p>
//                         <p className="text-xs text-gray-400">{label}</p>
//                       </div>
//                     ))}
//                   </div>

//                   <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
//                     <div className="flex items-center gap-2 mb-5">
//                       <TrendingUp className="w-4 h-4 text-[#012760]" />
//                       <p className="text-sm font-semibold text-[#012760]">Top Skills Across Ceipal</p>
//                     </div>
//                     {analytics?.topSkills?.length ? (
//                       <div className="space-y-4">
//                         {analytics.topSkills.map(({ skill, count }, i) => {
//                           const max = analytics.topSkills[0].count || 1;
//                           const colors = ["bg-[#012760]", "bg-blue-500", "bg-indigo-500", "bg-orange-400", "bg-purple-500"];
//                           return (
//                             <div key={skill}>
//                               <div className="flex justify-between text-xs mb-1.5">
//                                 <span className="text-gray-700 font-medium truncate max-w-[220px]">{skill}</span>
//                                 <span className="text-gray-400 ml-2 flex-shrink-0">{count.toLocaleString()} resumes</span>
//                               </div>
//                               <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
//                                 <div className={`h-full ${colors[i % colors.length]} rounded-full`} style={{ width: `${(count / max) * 100}%` }} />
//                               </div>
//                             </div>
//                           );
//                         })}
//                       </div>
//                     ) : (
//                       <p className="text-xs text-gray-400">
//                         Backend hasn't returned top-skills aggregation yet — see the `/ceipal/analytics`
//                         stub in ceipalRoutes.example.js.
//                       </p>
//                     )}
//                   </div>
//                 </>
//               )}
//             </div>
//           )}
//         </div>
//       </div>

//       <Footer />
//     </>
//   );
// };

// export default CeipalDBPage;









// import { useState, useEffect, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   ArrowLeft, Search, X, CheckCircle, AlertCircle,
//   Loader2, TrendingUp, RefreshCw, Download, ExternalLink,
//   BarChart2, Database, RotateCcw,
// } from "lucide-react";
// import { USER_API_END_POINT } from "@/utils/constant";
// import Footer from "../Footer";
// import Pagination from "./Pagination";

// /**
//  * ─────────────────────────────────────────────────────────────────────────
//  * WHAT CHANGED IN THIS VERSION
//  * ─────────────────────────────────────────────────────────────────────────
//  * 1. Search fields moved into the hero (header) area, matching Ceipal's
//  *    own "Applicant Search" bar — but only the MAIN fields (Keywords,
//  *    Job Title, Country, State, City), not the full Ceipal panel
//  *    (String Builder, radius search, recent/saved history, Exclude tab).
//  * 2. No auto-load on mount and no auto-refetch while typing. Data is
//  *    only fetched once the user clicks Search (or presses Enter) —
//  *    before that, the page shows a prompt instead of pulling 300k+ rows.
//  * 3. Visa/Experience dropdown filter panel from the previous version is
//  *    removed — replaced by the header fields above.
//  *
//  * Ceipal JSON -> UI field mapping (per the payload you shared):
//  *   name          <- first_name + middle_name + last_name
//  *   jobTitle      <- job_title
//  *   experience    <- experience
//  *   skills        <- skills / primary_skills
//  *   visaType      <- work_authorization
//  *   location      <- city, state, country
//  *   updatedAt     <- NOT confirmed in the real payload — check the exact
//  *                    field name (resume_updated_on / modified_date / etc.)
//  *   resumeUrl     <- NOT in the sample payload — linking to Ceipal profile
//  *                    instead of an inline preview until that's wired up.
//  * ─────────────────────────────────────────────────────────────────────────
//  */

// const TABS = [
//   { id: "search", label: "Search Hirees", icon: Search },
//   { id: "analytics", label: "Analytics", icon: BarChart2 },
// ];

// function mapCeipalRecordToCandidate(item, index) {
//   const rawSkills = item.primary_skills || item.skills || "";
//   const skills = Array.isArray(rawSkills)
//     ? rawSkills
//     : String(rawSkills).split(",").map((s) => s.trim()).filter(Boolean);

//   return {
//     id: item.applicant_id || item.id || `ceipal-${index}`,
//     name: [item.first_name, item.middle_name, item.last_name].filter(Boolean).join(" ") || "Unknown",
//     email: item.email_address || null,
//     phone: item.mobile_number || item.work_phone_number || item.home_phone_number || null,
//     jobTitle: item.job_title || "",
//     experience: item.experience ? Number(item.experience) : null,
//     skills,
//     visaType: item.work_authorization || "Unspecified",
//     location: [item.city, item.state, item.country].filter(Boolean).join(", "),
//     linkedinUrl: item.linkedin_profile_url || null,
//     ceipalProfileUrl: item.profile_url || null, // confirm real field name with Ceipal
//     updatedAt: item.resume_updated_on || item.modified_date || null,
//   };
// }

// const CeipalDBPage = () => {
//   const navigate = useNavigate();
//   const [activeTab, setActiveTab] = useState("search");

//   // ── Header search fields (Ceipal's main fields only) ──────────────────
//   const [keywords, setKeywords] = useState("");
//   const [jobTitleInput, setJobTitleInput] = useState("");
//   const [country, setCountry] = useState("");
//   const [stateInput, setStateInput] = useState("");
//   const [city, setCity] = useState("");

//   // The params actually sent to the backend — only updated when Search
//   // is clicked, so typing doesn't trigger fetches.
//   const [appliedFilters, setAppliedFilters] = useState(null);
//   const hasSearched = appliedFilters !== null;

//   // Results
//   const [candidates, setCandidates] = useState([]);
//   const [totalCount, setTotalCount] = useState(0);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);

//   // Pagination — server-side
//   const [currentPage, setCurrentPage] = useState(1);
//   const [pageSize, setPageSize] = useState(20);

//   // Row selection (current page only)
//   const [selectedIds, setSelectedIds] = useState(new Set());
//   const toggleSelectAll = () => {
//     setSelectedIds((prev) =>
//       prev.size === candidates.length ? new Set() : new Set(candidates.map((c) => c.id))
//     );
//   };
//   const toggleRow = (id) => {
//     setSelectedIds((prev) => {
//       const next = new Set(prev);
//       next.has(id) ? next.delete(id) : next.add(id);
//       return next;
//     });
//   };

//   // Analytics (separate aggregate call, not derived from `candidates`)
//   const [analytics, setAnalytics] = useState(null);
//   const [analyticsLoading, setAnalyticsLoading] = useState(false);
//   const [analyticsError, setAnalyticsError] = useState(null);

//   const fetchCandidates = useCallback(async (filters, page, size) => {
//     setLoading(true);
//     setError(null);
//     try {
//       const params = new URLSearchParams({
//         page,
//         pageSize: size,
//         ...(filters.keywords ? { q: filters.keywords } : {}),
//         ...(filters.jobTitle ? { jobTitle: filters.jobTitle } : {}),
//         ...(filters.country ? { country: filters.country } : {}),
//         ...(filters.state ? { state: filters.state } : {}),
//         ...(filters.city ? { city: filters.city } : {}),
//       });

//       const res = await fetch(`${USER_API_END_POINT}/ceipal/candidates?${params}`, {
//         credentials: "include",
//       });
//       const data = await res.json();

//       if (!data.success) throw new Error(data.message || "Failed to load Ceipal candidates");

//       const list = Array.isArray(data.results) ? data.results : data.data || [];
//       setCandidates(list.map(mapCeipalRecordToCandidate));
//       setTotalCount(data.total ?? list.length);
//     } catch (err) {
//       console.error("Error fetching Ceipal candidates:", err);
//       setError(err.message || "Failed to load Ceipal candidates");
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   // Only fetch when the user searches, or when they change page/pageSize
//   // AFTER a search has already been run. Nothing fetches on mount.
//   useEffect(() => {
//     if (!hasSearched) return;
//     fetchCandidates(appliedFilters, currentPage, pageSize);
//   }, [hasSearched, appliedFilters, currentPage, pageSize, fetchCandidates]);

//   useEffect(() => {
//     setSelectedIds(new Set());
//   }, [candidates]);

//   useEffect(() => {
//     if (activeTab !== "analytics") return;
//     const fetchAnalytics = async () => {
//       setAnalyticsLoading(true);
//       setAnalyticsError(null);
//       try {
//         const res = await fetch(`${USER_API_END_POINT}/ceipal/analytics`, { credentials: "include" });
//         const data = await res.json();
//         if (!data.success) throw new Error(data.message || "Failed to load analytics");
//         setAnalytics(data.analytics);
//       } catch (err) {
//         setAnalyticsError(err.message || "Failed to load analytics");
//       } finally {
//         setAnalyticsLoading(false);
//       }
//     };
//     fetchAnalytics();
//   }, [activeTab]);

//   const handleSearch = () => {
//     setCurrentPage(1);
//     setAppliedFilters({
//       keywords: keywords.trim(),
//       jobTitle: jobTitleInput.trim(),
//       country: country.trim(),
//       state: stateInput.trim(),
//       city: city.trim(),
//     });
//   };

//   const handleReset = () => {
//     setKeywords("");
//     setJobTitleInput("");
//     setCountry("");
//     setStateInput("");
//     setCity("");
//   };

//   const handleRefresh = () => {
//     if (!hasSearched) return;
//     fetchCandidates(appliedFilters, currentPage, pageSize);
//   };

//   const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
//   const handlePageChange = (page) => setCurrentPage(page);
//   const handlePageSizeChange = (size) => {
//     setPageSize(parseInt(size, 10));
//     setCurrentPage(1);
//   };

//   const inputCls =
//     "w-full text-sm text-gray-700 placeholder-gray-400 bg-transparent outline-none";
//   const fieldWrapCls =
//     "flex flex-col gap-1 bg-white rounded-xl px-3 py-2 shadow-sm border border-white/10";
//   const labelCls = "text-[9px] font-bold uppercase tracking-widest text-gray-400";

//   return (
//     <>
//       <div className="min-h-screen bg-[#f8f9fc]">
//         {/* ── Hero ──────────────────────────────────────────────────────── */}
//         <div className="relative w-full overflow-hidden">
//           <div className="absolute inset-0 bg-[#012760]" />
//           <div
//             className="absolute inset-0 opacity-[0.07]"
//             style={{
//               backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)",
//               backgroundSize: "28px 28px",
//             }}
//           />
//           <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-400 rounded-full opacity-10 blur-3xl" />
//           <div className="absolute -bottom-10 right-20 w-80 h-80 bg-blue-300 rounded-full opacity-10 blur-3xl" />
//           <div className="relative w-full px-4 md:px-8 lg:px-10 py-10 md:py-12">
//             <button
//               onClick={() => navigate(-1)}
//               className="flex items-center gap-2 text-white/50 hover:text-white text-xs font-medium mb-5 transition-colors"
//             >
//               <ArrowLeft className="w-3.5 h-3.5" />
//               Back to Candidates
//             </button>
//             <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
//               <div>
//                 <div className="flex items-center gap-2 mb-2">
//                   <span className="text-[10px] font-bold uppercase tracking-widest text-blue-300">Hirees</span>
//                   <span className="w-1 h-1 rounded-full bg-blue-400 opacity-60" />
//                   <span className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Applicant Database</span>
//                 </div>
//                 <h1 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-2">
//                   Hirees <span className="text-blue-300">Candidate</span> Search
//                 </h1>
//                 <p className="text-white/50 text-sm max-w-xl">
//                   Search across your full Hirees applicant database in real time — results are fetched
//                   page by page, so this stays fast even with 300,000+ resumes.
//                 </p>
//               </div>
//               {hasSearched && (
//                 <div className="flex items-center gap-2 bg-white/10 border border-white/10 rounded-xl px-4 py-2">
//                   <Database className="w-3.5 h-3.5 text-white/60" />
//                   <span className="text-white text-xs font-semibold">
//                     {totalCount ? totalCount.toLocaleString() : "—"} found
//                   </span>
//                 </div>
//               )}
//             </div>

//             {/* ── Hirees-style search fields (main fields only) ── */}
//             <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
//               <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
//                 <div className={`${fieldWrapCls} md:col-span-2`}>
//                   <label className={labelCls}>Keywords</label>
//                   <input
//                     type="text"
//                     placeholder="e.g. React, JavaScript"
//                     className={inputCls}
//                     value={keywords}
//                     onChange={(e) => setKeywords(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//                 <div className={fieldWrapCls}>
//                   <label className={labelCls}>Job Title</label>
//                   <input
//                     type="text"
//                     placeholder='e.g. "Java Developer"'
//                     className={inputCls}
//                     value={jobTitleInput}
//                     onChange={(e) => setJobTitleInput(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//                 <div className={fieldWrapCls}>
//                   <label className={labelCls}>Country</label>
//                   <input
//                     type="text"
//                     placeholder="United States"
//                     className={inputCls}
//                     value={country}
//                     onChange={(e) => setCountry(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//                 <div className={fieldWrapCls}>
//                   <label className={labelCls}>State</label>
//                   <input
//                     type="text"
//                     placeholder="New Jersey"
//                     className={inputCls}
//                     value={stateInput}
//                     onChange={(e) => setStateInput(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//               </div>
//               <div className="flex items-center gap-3 flex-wrap">
//                 <div className={`${fieldWrapCls} flex-1 min-w-[180px]`}>
//                   <label className={labelCls}>City</label>
//                   <input
//                     type="text"
//                     placeholder="Jersey City"
//                     className={inputCls}
//                     value={city}
//                     onChange={(e) => setCity(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//                 <button
//                   onClick={handleSearch}
//                   className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
//                 >
//                   <Search className="w-4 h-4" />
//                   Search
//                 </button>
//                 <button
//                   onClick={handleReset}
//                   className="flex items-center gap-2 px-4 py-3 border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-sm font-medium rounded-xl transition-colors"
//                 >
//                   <RotateCcw className="w-3.5 h-3.5" />
//                   Reset
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* ── Tab bar ───────────────────────────────────────────────────── */}
//         <div className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
//           <div className="w-full px-4 md:px-8 lg:px-10 flex gap-0">
//             {TABS.map(({ id, label, icon: Icon }) => (
//               <button
//                 key={id}
//                 onClick={() => setActiveTab(id)}
//                 className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap
//                   ${activeTab === id
//                     ? "border-[#012760] text-[#012760]"
//                     : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200"
//                   }`}
//               >
//                 <Icon className="w-3.5 h-3.5" />
//                 {label}
//               </button>
//             ))}
//           </div>
//         </div>

//         <div className="w-full px-4 md:px-8 lg:px-10 py-8">
//           {/* ══════════════════════ TAB: Search ══════════════════════ */}
//           {activeTab === "search" && (
//             <div>
//               {!hasSearched ? (
//                 <div className="bg-white rounded-2xl border border-gray-100 p-14 text-center shadow-sm">
//                   <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
//                     <Search className="w-5 h-5 text-gray-400" />
//                   </div>
//                   <p className="text-gray-700 font-semibold mb-1">Search Ceipal to see results</p>
//                   <p className="text-gray-400 text-sm">
//                     Use the fields above (keywords, job title, or location) and hit Search —
//                     nothing loads automatically since there are 300,000+ resumes in Ceipal.
//                   </p>
//                 </div>
//               ) : (
//                 <>
//                   <div className="flex items-center justify-between mb-4">
//                     <p className="text-sm font-semibold text-gray-700">
//                       {loading ? (
//                         <span className="flex items-center gap-2 text-gray-400">
//                           <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
//                         </span>
//                       ) : (
//                         <>
//                           {totalCount.toLocaleString()}{" "}
//                           <span className="font-normal text-gray-400">
//                             {totalCount === 1 ? "candidate" : "candidates"} found
//                           </span>
//                         </>
//                       )}
//                     </p>
//                     <div className="flex items-center gap-3">
//                       <button
//                         onClick={handleRefresh}
//                         className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all"
//                       >
//                         <RefreshCw className="w-3.5 h-3.5" />
//                         Refresh
//                       </button>
//                       <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all">
//                         <Download className="w-3.5 h-3.5" />
//                         Export
//                       </button>
//                     </div>
//                   </div>

//                   {error && (
//                     <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-4 flex items-center gap-3">
//                       <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
//                       <p className="text-sm text-red-700">{error}</p>
//                       <button
//                         onClick={handleRefresh}
//                         className="ml-auto text-xs font-medium text-red-600 hover:text-red-800 underline"
//                       >
//                         Retry
//                       </button>
//                     </div>
//                   )}

//                   {!loading && candidates.length === 0 && !error ? (
//                     <div className="bg-white rounded-2xl border border-gray-100 p-14 text-center shadow-sm">
//                       <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
//                         <X className="w-5 h-5 text-gray-400" />
//                       </div>
//                       <p className="text-gray-700 font-semibold mb-1">No candidates found</p>
//                       <p className="text-gray-400 text-sm">Try different keywords or clearing a field</p>
//                     </div>
//                   ) : (
//                     <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
//                       <div
//                         className="overflow-auto"
//                         style={{ maxHeight: "calc(100vh - 320px)" }}
//                       >
//                         <table className="min-w-full text-sm">
//                           <thead className="sticky top-0 z-10">
//                             <tr className="bg-[#eef1fb] border-b border-[#012760]/10">
//                               <th className="w-10 px-3 py-3">
//                                 <input
//                                   type="checkbox"
//                                   checked={candidates.length > 0 && selectedIds.size === candidates.length}
//                                   onChange={toggleSelectAll}
//                                   className="rounded border-gray-300 accent-[#012760]"
//                                 />
//                               </th>
//                               {[
//                                 "NAME", "EMAIL ID", "CONTACT NUMBER", "JOB TITLE", "EXPERIENCE",
//                                 "CURRENT EMPLOYER", "LOCATION", "WORK AUTHORIZATION",
//                                 "NOTICE PERIOD", "RESUME UPDATED ON", "",
//                               ].map((label) => (
//                                 <th
//                                   key={label}
//                                   className="px-3 py-3 text-left text-[11px] font-semibold text-[#012760] tracking-wide whitespace-nowrap"
//                                 >
//                                   {label}
//                                 </th>
//                               ))}
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {candidates.map((c) => (
//                               <tr
//                                 key={c.id}
//                                 className="border-b border-gray-50 hover:bg-[#012760]/5 transition-colors"
//                               >
//                                 <td className="px-3 py-2.5">
//                                   <input
//                                     type="checkbox"
//                                     checked={selectedIds.has(c.id)}
//                                     onChange={() => toggleRow(c.id)}
//                                     className="rounded border-gray-300 accent-[#012760]"
//                                   />
//                                 </td>
//                                 <td className="px-3 py-2.5 text-[#012760] font-medium whitespace-nowrap">{c.name}</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.email || "N/A"}</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.phone || "N/A"}</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap max-w-[160px] truncate" title={c.jobTitle}>
//                                   {c.jobTitle || "N/A"}
//                                 </td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
//                                   {c.experience != null ? `${c.experience} yrs` : "N/A"}
//                                 </td>
//                                 {/* Not present in Ceipal payload — confirm field name once available */}
//                                 <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">N/A</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap max-w-[180px] truncate" title={c.location}>
//                                   {c.location || "N/A"}
//                                 </td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.visaType}</td>
//                                 {/* Not present in Ceipal payload — confirm field name once available */}
//                                 <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">N/A</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.updatedAt || "N/A"}</td>
//                                 <td className="px-3 py-2.5 whitespace-nowrap">
//                                   {c.ceipalProfileUrl || c.linkedinUrl ? (
//                                     <a
//                                       href={c.ceipalProfileUrl || c.linkedinUrl}
//                                       target="_blank"
//                                       rel="noreferrer"
//                                       className="flex items-center gap-1.5 text-[11px] border rounded-lg px-2.5 py-1 font-medium text-[#012760] border-[#012760] hover:bg-[#012760] hover:text-white transition-all w-fit"
//                                     >
//                                       <ExternalLink className="w-3 h-3" /> View
//                                     </a>
//                                   ) : (
//                                     <span className="text-[11px] text-gray-300">—</span>
//                                   )}
//                                 </td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </table>
//                       </div>
//                     </div>
//                   )}

//                   {totalCount > 0 && (
//                     <Pagination
//                       currentPage={currentPage}
//                       totalPages={totalPages}
//                       onPageChange={handlePageChange}
//                       pageSize={pageSize}
//                       totalItems={totalCount}
//                       onPageSizeChange={handlePageSizeChange}
//                     />
//                   )}
//                 </>
//               )}
//             </div>
//           )}

//           {/* ══════════════════════ TAB: Analytics ══════════════════════ */}
//           {activeTab === "analytics" && (
//             <div>
//               {analyticsLoading ? (
//                 <div className="flex items-center justify-center h-48 text-gray-400 gap-2">
//                   <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
//                 </div>
//               ) : analyticsError ? (
//                 <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-3">
//                   <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
//                   <p className="text-sm text-red-700">{analyticsError}</p>
//                 </div>
//               ) : (
//                 <>
//                   <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
//                     {[
//                       { label: "Total in Ceipal", value: analytics?.totalCount ?? "—", icon: Database, color: "text-[#012760]", bg: "bg-[#f0f4ff]" },
//                       { label: "Avg Experience", value: analytics?.avgExperience ? `${analytics.avgExperience}y` : "—", icon: TrendingUp, color: "text-purple-700", bg: "bg-purple-50" },
//                       { label: "US Citizens / GC", value: analytics?.citizenCount ?? "—", icon: CheckCircle, color: "text-green-700", bg: "bg-green-50" },
//                       { label: "Need Sponsorship", value: analytics?.sponsorshipCount ?? "—", icon: AlertCircle, color: "text-orange-600", bg: "bg-orange-50" },
//                     ].map(({ label, value, icon: Icon, color, bg }) => (
//                       <div key={label} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
//                         <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center mb-3`}>
//                           <Icon className={`w-4 h-4 ${color}`} />
//                         </div>
//                         <p className="text-2xl font-bold text-gray-800 mb-0.5">{value}</p>
//                         <p className="text-xs text-gray-400">{label}</p>
//                       </div>
//                     ))}
//                   </div>

//                   <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
//                     <div className="flex items-center gap-2 mb-5">
//                       <TrendingUp className="w-4 h-4 text-[#012760]" />
//                       <p className="text-sm font-semibold text-[#012760]">Top Skills Across Ceipal</p>
//                     </div>
//                     {analytics?.topSkills?.length ? (
//                       <div className="space-y-4">
//                         {analytics.topSkills.map(({ skill, count }, i) => {
//                           const max = analytics.topSkills[0].count || 1;
//                           const colors = ["bg-[#012760]", "bg-blue-500", "bg-indigo-500", "bg-orange-400", "bg-purple-500"];
//                           return (
//                             <div key={skill}>
//                               <div className="flex justify-between text-xs mb-1.5">
//                                 <span className="text-gray-700 font-medium truncate max-w-[220px]">{skill}</span>
//                                 <span className="text-gray-400 ml-2 flex-shrink-0">{count.toLocaleString()} resumes</span>
//                               </div>
//                               <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
//                                 <div className={`h-full ${colors[i % colors.length]} rounded-full`} style={{ width: `${(count / max) * 100}%` }} />
//                               </div>
//                             </div>
//                           );
//                         })}
//                       </div>
//                     ) : (
//                       <p className="text-xs text-gray-400">
//                         Backend hasn't returned top-skills aggregation yet — see the `/ceipal/analytics`
//                         stub in ceipalRoutes.example.js.
//                       </p>
//                     )}
//                   </div>
//                 </>
//               )}
//             </div>
//           )}
//         </div>
//       </div>

//       <Footer />
//     </>
//   );
// };

// export default CeipalDBPage;










// import { useState, useEffect, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   ArrowLeft, Search, X, CheckCircle, AlertCircle,
//   Loader2, TrendingUp, RefreshCw, Download, ExternalLink,
//   BarChart2, Database, RotateCcw,
// } from "lucide-react";
// import { USER_API_END_POINT } from "@/utils/constant";
// import Footer from "../Footer";
// import Pagination from "./Pagination";

// const TABS = [
//   { id: "search", label: "Search Hirees", icon: Search },
//   { id: "analytics", label: "Analytics", icon: BarChart2 },
// ];

// function mapCeipalRecordToCandidate(item, index) {
//   const rawSkills = item.primary_skills || item.skills || "";
//   const skills = Array.isArray(rawSkills)
//     ? rawSkills
//     : String(rawSkills).split(",").map((s) => s.trim()).filter(Boolean);

//   return {
//     id: item.applicant_id || item.id || `ceipal-${index}`,
//     name: [item.first_name, item.middle_name, item.last_name].filter(Boolean).join(" ") || "Unknown",
//     email: item.email_address || null,
//     phone: item.mobile_number || item.work_phone_number || item.home_phone_number || null,
//     jobTitle: item.job_title || "",
//     experience: item.experience ? Number(item.experience) : null,
//     skills,
//     visaType: item.work_authorization || "Unspecified",
//     location: [item.city, item.state, item.country].filter(Boolean).join(", "),
//     linkedinUrl: item.linkedin_profile_url || null,
//     ceipalProfileUrl: item.profile_url || null,
//     updatedAt: item.resume_updated_on || item.modified_date || null,
//   };
// }

// const CeipalDBPage = () => {
//   const navigate = useNavigate();
//   const [activeTab, setActiveTab] = useState("search");

//   const [keywords, setKeywords] = useState("");
//   const [jobTitleInput, setJobTitleInput] = useState("");
//   const [country, setCountry] = useState("");
//   const [stateInput, setStateInput] = useState("");
//   const [city, setCity] = useState("");

//   const [appliedFilters, setAppliedFilters] = useState(null);
//   const hasSearched = appliedFilters !== null;

//   const [candidates, setCandidates] = useState([]);
//   const [totalCount, setTotalCount] = useState(0);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);

//   const [currentPage, setCurrentPage] = useState(1);
//   const [pageSize, setPageSize] = useState(20);

//   const [selectedIds, setSelectedIds] = useState(new Set());
//   const toggleSelectAll = () => {
//     setSelectedIds((prev) =>
//       prev.size === candidates.length ? new Set() : new Set(candidates.map((c) => c.id))
//     );
//   };
//   const toggleRow = (id) => {
//     setSelectedIds((prev) => {
//       const next = new Set(prev);
//       next.has(id) ? next.delete(id) : next.add(id);
//       return next;
//     });
//   };

//   const [analytics, setAnalytics] = useState(null);
//   const [analyticsLoading, setAnalyticsLoading] = useState(false);
//   const [analyticsError, setAnalyticsError] = useState(null);

//   const fetchCandidates = useCallback(async (filters, page, size) => {
//     setLoading(true);
//     setError(null);
//     try {
//       const params = new URLSearchParams({
//         page,
//         pageSize: size,
//         ...(filters.keywords ? { q: filters.keywords } : {}),
//         ...(filters.jobTitle ? { jobTitle: filters.jobTitle } : {}),
//         ...(filters.country ? { country: filters.country } : {}),
//         ...(filters.state ? { state: filters.state } : {}),
//         ...(filters.city ? { city: filters.city } : {}),
//       });

//       const res = await fetch(`${USER_API_END_POINT}/ceipal/candidates?${params}`, {
//         credentials: "include",
//       });
//       const data = await res.json();

//       if (!data.success) throw new Error(data.message || "Failed to load Ceipal candidates");

//       const list = Array.isArray(data.results) ? data.results : data.data || [];
//       setCandidates(list.map(mapCeipalRecordToCandidate));
//       setTotalCount(data.total ?? list.length);
//     } catch (err) {
//       console.error("Error fetching Ceipal candidates:", err);
//       setError(err.message || "Failed to load Ceipal candidates");
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   useEffect(() => {
//     if (!hasSearched) return;
//     fetchCandidates(appliedFilters, currentPage, pageSize);
//   }, [hasSearched, appliedFilters, currentPage, pageSize, fetchCandidates]);

//   useEffect(() => {
//     setSelectedIds(new Set());
//   }, [candidates]);

//   useEffect(() => {
//     if (activeTab !== "analytics") return;
//     const fetchAnalytics = async () => {
//       setAnalyticsLoading(true);
//       setAnalyticsError(null);
//       try {
//         const res = await fetch(`${USER_API_END_POINT}/ceipal/analytics`, { credentials: "include" });
//         const data = await res.json();
//         if (!data.success) throw new Error(data.message || "Failed to load analytics");
//         setAnalytics(data.analytics);
//       } catch (err) {
//         setAnalyticsError(err.message || "Failed to load analytics");
//       } finally {
//         setAnalyticsLoading(false);
//       }
//     };
//     fetchAnalytics();
//   }, [activeTab]);

//   const handleSearch = () => {
//     setCurrentPage(1);
//     setAppliedFilters({
//       keywords: keywords.trim(),
//       jobTitle: jobTitleInput.trim(),
//       country: country.trim(),
//       state: stateInput.trim(),
//       city: city.trim(),
//     });
//   };

//   const handleReset = () => {
//     setKeywords("");
//     setJobTitleInput("");
//     setCountry("");
//     setStateInput("");
//     setCity("");
//   };

//   const handleRefresh = () => {
//     if (!hasSearched) return;
//     fetchCandidates(appliedFilters, currentPage, pageSize);
//   };

//   const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
//   const handlePageChange = (page) => setCurrentPage(page);
//   const handlePageSizeChange = (size) => {
//     setPageSize(parseInt(size, 10));
//     setCurrentPage(1);
//   };

//   const inputCls =
//     "w-full text-sm text-gray-700 placeholder-gray-400 bg-transparent outline-none";
//   const fieldWrapCls =
//     "flex flex-col gap-1 bg-white rounded-xl px-3 py-2 shadow-sm border border-white/10";
//   const labelCls = "text-[9px] font-bold uppercase tracking-widest text-gray-400";

//   return (
//     <>
//       <div className="min-h-screen bg-[#f8f9fc]">
//         {/* ── Hero ──────────────────────────────────────────────────────── */}
//         <div className="relative w-full overflow-hidden">
//           <div className="absolute inset-0 bg-[#012760]" />
//           <div
//             className="absolute inset-0 opacity-[0.07]"
//             style={{
//               backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)",
//               backgroundSize: "28px 28px",
//             }}
//           />
//           <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-400 rounded-full opacity-10 blur-3xl" />
//           <div className="absolute -bottom-10 right-20 w-80 h-80 bg-blue-300 rounded-full opacity-10 blur-3xl" />
//           <div className="relative w-full px-4 md:px-8 lg:px-10 py-10 md:py-12">
//             <button
//               onClick={() => navigate(-1)}
//               className="flex items-center gap-2 text-white/50 hover:text-white text-xs font-medium mb-5 transition-colors"
//             >
//               <ArrowLeft className="w-3.5 h-3.5" />
//               Back to Candidates
//             </button>
//             <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
//               <div>
//                 <div className="flex items-center gap-2 mb-2">
//                   <span className="text-[10px] font-bold uppercase tracking-widest text-blue-300">Hirees</span>
//                   <span className="w-1 h-1 rounded-full bg-blue-400 opacity-60" />
//                   <span className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Applicant Database</span>
//                 </div>
//                 <h1 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-2">
//                   Hirees <span className="text-blue-300">Candidate</span> Search
//                 </h1>
//                 <p className="text-white/50 text-sm max-w-xl">
//                   Search across your full Hirees applicant database in real time — results are fetched
//                   page by page, so this stays fast even with 300,000+ resumes.
//                 </p>
//               </div>
//               {hasSearched && (
//                 <div className="flex items-center gap-2 bg-white/10 border border-white/10 rounded-xl px-4 py-2">
//                   <Database className="w-3.5 h-3.5 text-white/60" />
//                   <span className="text-white text-xs font-semibold">
//                     {totalCount ? totalCount.toLocaleString() : "—"} found
//                   </span>
//                 </div>
//               )}
//             </div>

//             {/* ── Hirees-style search fields (main fields only) ── */}
//             <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
//                 <div className={fieldWrapCls}>
//                   <label className={labelCls}>Keywords</label>
//                   <input
//                     type="text"
//                     placeholder="e.g. React, JavaScript"
//                     className={inputCls}
//                     value={keywords}
//                     onChange={(e) => setKeywords(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//                 <div className={fieldWrapCls}>
//                   <label className={labelCls}>Job Title</label>
//                   <input
//                     type="text"
//                     placeholder='e.g. "Java Developer"'
//                     className={inputCls}
//                     value={jobTitleInput}
//                     onChange={(e) => setJobTitleInput(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//               </div>
//               <div className="flex items-center gap-3 flex-wrap">
//                 <div className={`${fieldWrapCls} flex-1 min-w-[160px]`}>
//                   <label className={labelCls}>Country</label>
//                   <input
//                     type="text"
//                     placeholder="United States"
//                     className={inputCls}
//                     value={country}
//                     onChange={(e) => setCountry(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//                 <div className={`${fieldWrapCls} flex-1 min-w-[160px]`}>
//                   <label className={labelCls}>State</label>
//                   <input
//                     type="text"
//                     placeholder="New Jersey"
//                     className={inputCls}
//                     value={stateInput}
//                     onChange={(e) => setStateInput(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//                 <div className={`${fieldWrapCls} flex-1 min-w-[160px]`}>
//                   <label className={labelCls}>City</label>
//                   <input
//                     type="text"
//                     placeholder="Jersey City"
//                     className={inputCls}
//                     value={city}
//                     onChange={(e) => setCity(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//                 <button
//                   onClick={handleSearch}
//                   className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
//                 >
//                   <Search className="w-4 h-4" />
//                   Search
//                 </button>
//                 <button
//                   onClick={handleReset}
//                   className="flex items-center gap-2 px-4 py-3 border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-sm font-medium rounded-xl transition-colors"
//                 >
//                   <RotateCcw className="w-3.5 h-3.5" />
//                   Reset
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* ── Tab bar ───────────────────────────────────────────────────── */}
//         <div className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
//           <div className="w-full px-4 md:px-8 lg:px-10 flex gap-0">
//             {TABS.map(({ id, label, icon: Icon }) => (
//               <button
//                 key={id}
//                 onClick={() => setActiveTab(id)}
//                 className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap
//                   ${activeTab === id
//                     ? "border-[#012760] text-[#012760]"
//                     : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200"
//                   }`}
//               >
//                 <Icon className="w-3.5 h-3.5" />
//                 {label}
//               </button>
//             ))}
//           </div>
//         </div>

//         <div className="w-full px-4 md:px-8 lg:px-10 py-8">
//           {/* ══════════════════════ TAB: Search ══════════════════════ */}
//           {activeTab === "search" && (
//             <div>
//               {!hasSearched ? (
//                 <div className="bg-white rounded-2xl border border-gray-100 p-14 text-center shadow-sm">
//                   <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
//                     <Search className="w-5 h-5 text-gray-400" />
//                   </div>
//                   <p className="text-gray-700 font-semibold mb-1">Search Hirees to see results</p>
//                   <p className="text-gray-400 text-sm">
//                     Use the fields above (keywords, job title, or location) and hit Search —
//                     nothing loads automatically since there are 300,000+ resumes.
//                   </p>
//                 </div>
//               ) : (
//                 <>
//                   <div className="flex items-center justify-between mb-4">
//                     <p className="text-sm font-semibold text-gray-700">
//                       {loading ? (
//                         <span className="flex items-center gap-2 text-gray-400">
//                           <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
//                         </span>
//                       ) : (
//                         <>
//                           {totalCount.toLocaleString()}{" "}
//                           <span className="font-normal text-gray-400">
//                             {totalCount === 1 ? "candidate" : "candidates"} found
//                           </span>
//                         </>
//                       )}
//                     </p>
//                     <div className="flex items-center gap-3">
//                       <button
//                         onClick={handleRefresh}
//                         className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all"
//                       >
//                         <RefreshCw className="w-3.5 h-3.5" />
//                         Refresh
//                       </button>
//                       <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all">
//                         <Download className="w-3.5 h-3.5" />
//                         Export
//                       </button>
//                     </div>
//                   </div>

//                   {error && (
//                     <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-4 flex items-center gap-3">
//                       <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
//                       <p className="text-sm text-red-700">{error}</p>
//                       <button
//                         onClick={handleRefresh}
//                         className="ml-auto text-xs font-medium text-red-600 hover:text-red-800 underline"
//                       >
//                         Retry
//                       </button>
//                     </div>
//                   )}

//                   {!loading && candidates.length === 0 && !error ? (
//                     <div className="bg-white rounded-2xl border border-gray-100 p-14 text-center shadow-sm">
//                       <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
//                         <X className="w-5 h-5 text-gray-400" />
//                       </div>
//                       <p className="text-gray-700 font-semibold mb-1">No candidates found</p>
//                       <p className="text-gray-400 text-sm">Try different keywords or clearing a field</p>
//                     </div>
//                   ) : (
//                     <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
//                       <div
//                         className="overflow-auto"
//                         style={{ maxHeight: "calc(100vh - 320px)" }}
//                       >
//                         <table className="min-w-full text-sm">
//                           <thead className="sticky top-0 z-10">
//                             <tr className="bg-[#eef1fb] border-b border-[#012760]/10">
//                               <th className="w-10 px-3 py-3">
//                                 <input
//                                   type="checkbox"
//                                   checked={candidates.length > 0 && selectedIds.size === candidates.length}
//                                   onChange={toggleSelectAll}
//                                   className="rounded border-gray-300 accent-[#012760]"
//                                 />
//                               </th>
//                               {[
//                                 "NAME", "EMAIL ID", "CONTACT NUMBER", "JOB TITLE", "EXPERIENCE",
//                                 "CURRENT EMPLOYER", "LOCATION", "WORK AUTHORIZATION",
//                                 "NOTICE PERIOD", "RESUME UPDATED ON", "",
//                               ].map((label) => (
//                                 <th
//                                   key={label}
//                                   className="px-3 py-3 text-left text-[11px] font-semibold text-[#012760] tracking-wide whitespace-nowrap"
//                                 >
//                                   {label}
//                                 </th>
//                               ))}
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {candidates.map((c) => (
//                               <tr
//                                 key={c.id}
//                                 className="border-b border-gray-50 hover:bg-[#012760]/5 transition-colors"
//                               >
//                                 <td className="px-3 py-2.5">
//                                   <input
//                                     type="checkbox"
//                                     checked={selectedIds.has(c.id)}
//                                     onChange={() => toggleRow(c.id)}
//                                     className="rounded border-gray-300 accent-[#012760]"
//                                   />
//                                 </td>
//                                 <td className="px-3 py-2.5 text-[#012760] font-medium whitespace-nowrap">{c.name}</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.email || "N/A"}</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.phone || "N/A"}</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap max-w-[160px] truncate" title={c.jobTitle}>
//                                   {c.jobTitle || "N/A"}
//                                 </td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
//                                   {c.experience != null ? `${c.experience} yrs` : "N/A"}
//                                 </td>
//                                 <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">N/A</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap max-w-[180px] truncate" title={c.location}>
//                                   {c.location || "N/A"}
//                                 </td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.visaType}</td>
//                                 <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">N/A</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.updatedAt || "N/A"}</td>
//                                 <td className="px-3 py-2.5 whitespace-nowrap">
//                                   {c.ceipalProfileUrl || c.linkedinUrl ? (
//                                     <a
//                                       href={c.ceipalProfileUrl || c.linkedinUrl}
//                                       target="_blank"
//                                       rel="noreferrer"
//                                       className="flex items-center gap-1.5 text-[11px] border rounded-lg px-2.5 py-1 font-medium text-[#012760] border-[#012760] hover:bg-[#012760] hover:text-white transition-all w-fit"
//                                     >
//                                       <ExternalLink className="w-3 h-3" /> View
//                                     </a>
//                                   ) : (
//                                     <span className="text-[11px] text-gray-300">—</span>
//                                   )}
//                                 </td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </table>
//                       </div>
//                     </div>
//                   )}

//                   {totalCount > 0 && (
//                     <Pagination
//                       currentPage={currentPage}
//                       totalPages={totalPages}
//                       onPageChange={handlePageChange}
//                       pageSize={pageSize}
//                       totalItems={totalCount}
//                       onPageSizeChange={handlePageSizeChange}
//                     />
//                   )}
//                 </>
//               )}
//             </div>
//           )}

//           {/* ══════════════════════ TAB: Analytics ══════════════════════ */}
//           {activeTab === "analytics" && (
//             <div>
//               {analyticsLoading ? (
//                 <div className="flex items-center justify-center h-48 text-gray-400 gap-2">
//                   <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
//                 </div>
//               ) : analyticsError ? (
//                 <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-3">
//                   <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
//                   <p className="text-sm text-red-700">{analyticsError}</p>
//                 </div>
//               ) : (
//                 <>
//                   <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
//                     {[
//                       { label: "Total in Ceipal", value: analytics?.totalCount ?? "—", icon: Database, color: "text-[#012760]", bg: "bg-[#f0f4ff]" },
//                       { label: "Avg Experience", value: analytics?.avgExperience ? `${analytics.avgExperience}y` : "—", icon: TrendingUp, color: "text-purple-700", bg: "bg-purple-50" },
//                       { label: "US Citizens / GC", value: analytics?.citizenCount ?? "—", icon: CheckCircle, color: "text-green-700", bg: "bg-green-50" },
//                       { label: "Need Sponsorship", value: analytics?.sponsorshipCount ?? "—", icon: AlertCircle, color: "text-orange-600", bg: "bg-orange-50" },
//                     ].map(({ label, value, icon: Icon, color, bg }) => (
//                       <div key={label} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
//                         <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center mb-3`}>
//                           <Icon className={`w-4 h-4 ${color}`} />
//                         </div>
//                         <p className="text-2xl font-bold text-gray-800 mb-0.5">{value}</p>
//                         <p className="text-xs text-gray-400">{label}</p>
//                       </div>
//                     ))}
//                   </div>

//                   <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
//                     <div className="flex items-center gap-2 mb-5">
//                       <TrendingUp className="w-4 h-4 text-[#012760]" />
//                       <p className="text-sm font-semibold text-[#012760]">Top Skills Across Ceipal</p>
//                     </div>
//                     {analytics?.topSkills?.length ? (
//                       <div className="space-y-4">
//                         {analytics.topSkills.map(({ skill, count }, i) => {
//                           const max = analytics.topSkills[0].count || 1;
//                           const colors = ["bg-[#012760]", "bg-blue-500", "bg-indigo-500", "bg-orange-400", "bg-purple-500"];
//                           return (
//                             <div key={skill}>
//                               <div className="flex justify-between text-xs mb-1.5">
//                                 <span className="text-gray-700 font-medium truncate max-w-[220px]">{skill}</span>
//                                 <span className="text-gray-400 ml-2 flex-shrink-0">{count.toLocaleString()} resumes</span>
//                               </div>
//                               <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
//                                 <div className={`h-full ${colors[i % colors.length]} rounded-full`} style={{ width: `${(count / max) * 100}%` }} />
//                               </div>
//                             </div>
//                           );
//                         })}
//                       </div>
//                     ) : (
//                       <p className="text-xs text-gray-400">
//                         Backend hasn't returned top-skills aggregation yet — see the `/ceipal/analytics`
//                         stub in ceipalRoutes.example.js.
//                       </p>
//                     )}
//                   </div>
//                 </>
//               )}
//             </div>
//           )}
//         </div>
//       </div>

//       <Footer />
//     </>
//   );
// };

// export default CeipalDBPage;









// import { useState, useEffect, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   ArrowLeft, Search, X, CheckCircle, AlertCircle,
//   Loader2, TrendingUp, RefreshCw, Download, ExternalLink,
//   BarChart2, Database, RotateCcw,
// } from "lucide-react";
// import { USER_API_END_POINT } from "@/utils/constant";
// import Footer from "../Footer";
// import Pagination from "./Pagination";

// const TABS = [
//   { id: "search", label: "Search Hirees", icon: Search },
//   { id: "analytics", label: "Analytics", icon: BarChart2 },
// ];

// function mapCeipalRecordToCandidate(item, index) {
//   const rawSkills = item.primary_skills || item.skills || "";
//   const skills = Array.isArray(rawSkills)
//     ? rawSkills
//     : String(rawSkills).split(",").map((s) => s.trim()).filter(Boolean);

//   return {
//     id: item.applicant_id || item.id || `ceipal-${index}`,
//     name: [item.first_name, item.middle_name, item.last_name].filter(Boolean).join(" ") || "Unknown",
//     email: item.email_address || null,
//     phone: item.mobile_number || item.work_phone_number || item.home_phone_number || null,
//     jobTitle: item.job_title || "",
//     experience: item.experience ? Number(item.experience) : null,
//     skills,
//     visaType: item.work_authorization || "Unspecified",
//     location: [item.city, item.state, item.country].filter(Boolean).join(", "),
//     linkedinUrl: item.linkedin_profile_url || null,
//     ceipalProfileUrl: item.profile_url || null,
//     updatedAt: item.resume_updated_on || item.modified_date || null,
//   };
// }

// const CeipalDBPage = () => {
//   const navigate = useNavigate();
//   const [activeTab, setActiveTab] = useState("search");

//   const [keywords, setKeywords] = useState("");
//   const [jobTitleInput, setJobTitleInput] = useState("");
//   const [country, setCountry] = useState("");
//   const [stateInput, setStateInput] = useState("");
//   const [city, setCity] = useState("");

//   const [appliedFilters, setAppliedFilters] = useState(null);
//   const hasSearched = appliedFilters !== null;

//   const [candidates, setCandidates] = useState([]);
//   const [totalCount, setTotalCount] = useState(0);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);

//   const [currentPage, setCurrentPage] = useState(1);
//   const [pageSize, setPageSize] = useState(20);

//   const [selectedIds, setSelectedIds] = useState(new Set());
//   const toggleSelectAll = () => {
//     setSelectedIds((prev) =>
//       prev.size === candidates.length ? new Set() : new Set(candidates.map((c) => c.id))
//     );
//   };
//   const toggleRow = (id) => {
//     setSelectedIds((prev) => {
//       const next = new Set(prev);
//       next.has(id) ? next.delete(id) : next.add(id);
//       return next;
//     });
//   };

//   const [analytics, setAnalytics] = useState(null);
//   const [analyticsLoading, setAnalyticsLoading] = useState(false);
//   const [analyticsError, setAnalyticsError] = useState(null);

//   const fetchCandidates = useCallback(async (filters, page, size) => {
//     setLoading(true);
//     setError(null);
//     try {
//       const params = new URLSearchParams({
//   page,
//   pageSize: size,

//   ...(filters.keywords ? { skills: filters.keywords } : {}),

//   ...(filters.jobTitle
//     ? { positionTitle: filters.jobTitle }
//     : {}),

//   ...(filters.country ? { country: filters.country } : {}),
//   ...(filters.state ? { state: filters.state } : {}),
//   ...(filters.city ? { city: filters.city } : {}),
// });

//       const res = await fetch(`${USER_API_END_POINT}/ceipal/candidates?${params}`, {
//         credentials: "include",
//       });
//       const data = await res.json();

//       if (!data.success) throw new Error(data.message || "Failed to load Ceipal candidates");

//       const list = Array.isArray(data.results) ? data.results : data.data || [];
//       setCandidates(list.map(mapCeipalRecordToCandidate));
//       setTotalCount(data.total ?? list.length);
//     } catch (err) {
//       console.error("Error fetching Ceipal candidates:", err);
//       setError(err.message || "Failed to load Ceipal candidates");
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   useEffect(() => {
//     if (!hasSearched) return;
//     fetchCandidates(appliedFilters, currentPage, pageSize);
//   }, [hasSearched, appliedFilters, currentPage, pageSize, fetchCandidates]);

//   useEffect(() => {
//     setSelectedIds(new Set());
//   }, [candidates]);

//   useEffect(() => {
//     if (activeTab !== "analytics") return;
//     const fetchAnalytics = async () => {
//       setAnalyticsLoading(true);
//       setAnalyticsError(null);
//       try {
//         const res = await fetch(`${USER_API_END_POINT}/ceipal/analytics`, { credentials: "include" });
//         const data = await res.json();
//         if (!data.success) throw new Error(data.message || "Failed to load analytics");
//         setAnalytics(data.analytics);
//       } catch (err) {
//         setAnalyticsError(err.message || "Failed to load analytics");
//       } finally {
//         setAnalyticsLoading(false);
//       }
//     };
//     fetchAnalytics();
//   }, [activeTab]);

//   const handleSearch = () => {
//     setCurrentPage(1);
//     setAppliedFilters({
//       keywords: keywords.trim(),
//       jobTitle: jobTitleInput.trim(),
//       country: country.trim(),
//       state: stateInput.trim(),
//       city: city.trim(),
//     });
//   };

//   const handleReset = () => {
//     setKeywords("");
//     setJobTitleInput("");
//     setCountry("");
//     setStateInput("");
//     setCity("");
//   };

//   const handleRefresh = () => {
//     if (!hasSearched) return;
//     fetchCandidates(appliedFilters, currentPage, pageSize);
//   };

//   const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
//   const handlePageChange = (page) => setCurrentPage(page);
//   const handlePageSizeChange = (size) => {
//     setPageSize(parseInt(size, 10));
//     setCurrentPage(1);
//   };

//   const inputCls =
//     "w-full text-sm text-gray-700 placeholder-gray-400 bg-transparent outline-none";
//   const fieldWrapCls =
//     "flex flex-col gap-1 bg-white rounded-xl px-3 py-2 shadow-sm border border-white/10";
//   const labelCls = "text-[9px] font-bold uppercase tracking-widest text-gray-400";

//   return (
//     <>
//       <div className="min-h-screen bg-[#f8f9fc]">
//         {/* ── Hero ──────────────────────────────────────────────────────── */}
//         <div className="relative w-full overflow-hidden">
//           <div className="absolute inset-0 bg-[#012760]" />
//           <div
//             className="absolute inset-0 opacity-[0.07]"
//             style={{
//               backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)",
//               backgroundSize: "28px 28px",
//             }}
//           />
//           <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-400 rounded-full opacity-10 blur-3xl" />
//           <div className="absolute -bottom-10 right-20 w-80 h-80 bg-blue-300 rounded-full opacity-10 blur-3xl" />
//           <div className="relative w-full px-4 md:px-8 lg:px-10 py-10 md:py-12">
//             <button
//               onClick={() => navigate(-1)}
//               className="flex items-center gap-2 text-white/50 hover:text-white text-xs font-medium mb-5 transition-colors"
//             >
//               <ArrowLeft className="w-3.5 h-3.5" />
//               Back to Candidates
//             </button>
//             <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
//               <div>
//                 <div className="flex items-center gap-2 mb-2">
//                   <span className="text-[10px] font-bold uppercase tracking-widest text-blue-300">Hirees</span>
//                   <span className="w-1 h-1 rounded-full bg-blue-400 opacity-60" />
//                   <span className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Applicant Database</span>
//                 </div>
//                 <h1 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-2">
//                   Hirees <span className="text-blue-300">Candidate</span> Search
//                 </h1>
//                 <p className="text-white/50 text-sm max-w-xl">
//                   Search across your full Hirees applicant database in real time — results are fetched
//                   page by page, so this stays fast even with 300,000+ resumes.
//                 </p>
//               </div>
//               {hasSearched && (
//                 <div className="flex items-center gap-2 bg-white/10 border border-white/10 rounded-xl px-4 py-2">
//                   <Database className="w-3.5 h-3.5 text-white/60" />
//                   <span className="text-white text-xs font-semibold">
//                     {totalCount ? totalCount.toLocaleString() : "—"} found
//                   </span>
//                 </div>
//               )}
//             </div>

//             {/* ── Hirees-style search fields (main fields only) ── */}
//             <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
//                 <div className={fieldWrapCls}>
//                   <label className={labelCls}>Keywords</label>
//                   <input
//                     type="text"
//                     placeholder="e.g. React, JavaScript"
//                     className={inputCls}
//                     value={keywords}
//                     onChange={(e) => setKeywords(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//                 <div className={fieldWrapCls}>
//                   <label className={labelCls}>Job Title</label>
//                   <input
//                     type="text"
//                     placeholder='e.g. "Java Developer"'
//                     className={inputCls}
//                     value={jobTitleInput}
//                     onChange={(e) => setJobTitleInput(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//               </div>
//               <div className="flex items-center gap-3 flex-wrap">
//                 <div className={`${fieldWrapCls} flex-1 min-w-[160px]`}>
//                   <label className={labelCls}>Country</label>
//                   <input
//                     type="text"
//                     placeholder="United States"
//                     className={inputCls}
//                     value={country}
//                     onChange={(e) => setCountry(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//                 <div className={`${fieldWrapCls} flex-1 min-w-[160px]`}>
//                   <label className={labelCls}>State</label>
//                   <input
//                     type="text"
//                     placeholder="New Jersey"
//                     className={inputCls}
//                     value={stateInput}
//                     onChange={(e) => setStateInput(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//                 <div className={`${fieldWrapCls} flex-1 min-w-[160px]`}>
//                   <label className={labelCls}>City</label>
//                   <input
//                     type="text"
//                     placeholder="Jersey City"
//                     className={inputCls}
//                     value={city}
//                     onChange={(e) => setCity(e.target.value)}
//                     onKeyDown={(e) => e.key === "Enter" && handleSearch()}
//                   />
//                 </div>
//                 <button
//                   onClick={handleSearch}
//                   className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
//                 >
//                   <Search className="w-4 h-4" />
//                   Search
//                 </button>
//                 <button
//                   onClick={handleReset}
//                   className="flex items-center gap-2 px-4 py-3 border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-sm font-medium rounded-xl transition-colors"
//                 >
//                   <RotateCcw className="w-3.5 h-3.5" />
//                   Reset
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* ── Tab bar ───────────────────────────────────────────────────── */}
//         <div className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
//           <div className="w-full px-4 md:px-8 lg:px-10 flex gap-0">
//             {TABS.map(({ id, label, icon: Icon }) => (
//               <button
//                 key={id}
//                 onClick={() => setActiveTab(id)}
//                 className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap
//                   ${activeTab === id
//                     ? "border-[#012760] text-[#012760]"
//                     : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200"
//                   }`}
//               >
//                 <Icon className="w-3.5 h-3.5" />
//                 {label}
//               </button>
//             ))}
//           </div>
//         </div>

//         <div className="w-full px-4 md:px-8 lg:px-10 py-8">
//           {/* ══════════════════════ TAB: Search ══════════════════════ */}
//           {activeTab === "search" && (
//             <div>
//               {!hasSearched ? (
//                 <div className="bg-white rounded-2xl border border-gray-100 p-14 text-center shadow-sm">
//                   <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
//                     <Search className="w-5 h-5 text-gray-400" />
//                   </div>
//                   <p className="text-gray-700 font-semibold mb-1">Search Hirees to see results</p>
//                   <p className="text-gray-400 text-sm">
//                     Use the fields above (keywords, job title, or location) and hit Search —
//                     nothing loads automatically since there are 300,000+ resumes.
//                   </p>
//                 </div>
//               ) : (
//                 <>
//                   <div className="flex items-center justify-between mb-4">
//                     <p className="text-sm font-semibold text-gray-700">
//                       {loading ? (
//                         <span className="flex items-center gap-2 text-gray-400">
//                           <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
//                         </span>
//                       ) : (
//                         <>
//                           {totalCount.toLocaleString()}{" "}
//                           <span className="font-normal text-gray-400">
//                             {totalCount === 1 ? "candidate" : "candidates"} found
//                           </span>
//                         </>
//                       )}
//                     </p>
//                     <div className="flex items-center gap-3">
//                       <button
//                         onClick={handleRefresh}
//                         className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all"
//                       >
//                         <RefreshCw className="w-3.5 h-3.5" />
//                         Refresh
//                       </button>
//                       <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all">
//                         <Download className="w-3.5 h-3.5" />
//                         Export
//                       </button>
//                     </div>
//                   </div>

//                   {error && (
//                     <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-4 flex items-center gap-3">
//                       <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
//                       <p className="text-sm text-red-700">{error}</p>
//                       <button
//                         onClick={handleRefresh}
//                         className="ml-auto text-xs font-medium text-red-600 hover:text-red-800 underline"
//                       >
//                         Retry
//                       </button>
//                     </div>
//                   )}

//                   {!loading && candidates.length === 0 && !error ? (
//                     <div className="bg-white rounded-2xl border border-gray-100 p-14 text-center shadow-sm">
//                       <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
//                         <X className="w-5 h-5 text-gray-400" />
//                       </div>
//                       <p className="text-gray-700 font-semibold mb-1">No candidates found</p>
//                       <p className="text-gray-400 text-sm">Try different keywords or clearing a field</p>
//                     </div>
//                   ) : (
//                     <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
//                       <div
//                         className="overflow-auto"
//                         style={{ maxHeight: "calc(100vh - 320px)" }}
//                       >
//                         <table className="min-w-full text-sm">
//                           <thead className="sticky top-0 z-10">
//                             <tr className="bg-[#eef1fb] border-b border-[#012760]/10">
//                               <th className="w-10 px-3 py-3">
//                                 <input
//                                   type="checkbox"
//                                   checked={candidates.length > 0 && selectedIds.size === candidates.length}
//                                   onChange={toggleSelectAll}
//                                   className="rounded border-gray-300 accent-[#012760]"
//                                 />
//                               </th>
//                               {[
//                                 "NAME", "EMAIL ID", "CONTACT NUMBER", "JOB TITLE", "EXPERIENCE",
//                                 "CURRENT EMPLOYER", "LOCATION", "WORK AUTHORIZATION",
//                                 "NOTICE PERIOD", "RESUME UPDATED ON", "",
//                               ].map((label) => (
//                                 <th
//                                   key={label}
//                                   className="px-3 py-3 text-left text-[11px] font-semibold text-[#012760] tracking-wide whitespace-nowrap"
//                                 >
//                                   {label}
//                                 </th>
//                               ))}
//                             </tr>
//                           </thead>
//                           <tbody>
//                             {candidates.map((c) => (
//                               <tr
//                                 key={c.id}
//                                 className="border-b border-gray-50 hover:bg-[#012760]/5 transition-colors"
//                               >
//                                 <td className="px-3 py-2.5">
//                                   <input
//                                     type="checkbox"
//                                     checked={selectedIds.has(c.id)}
//                                     onChange={() => toggleRow(c.id)}
//                                     className="rounded border-gray-300 accent-[#012760]"
//                                   />
//                                 </td>
//                                 <td className="px-3 py-2.5 text-[#012760] font-medium whitespace-nowrap">{c.name}</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.email || "N/A"}</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.phone || "N/A"}</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap max-w-[160px] truncate" title={c.jobTitle}>
//                                   {c.jobTitle || "N/A"}
//                                 </td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
//                                   {c.experience != null ? `${c.experience} yrs` : "N/A"}
//                                 </td>
//                                 <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">N/A</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap max-w-[180px] truncate" title={c.location}>
//                                   {c.location || "N/A"}
//                                 </td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.visaType}</td>
//                                 <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">N/A</td>
//                                 <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.updatedAt || "N/A"}</td>
//                                 <td className="px-3 py-2.5 whitespace-nowrap">
//                                   {c.ceipalProfileUrl || c.linkedinUrl ? (
//                                     <a
//                                       href={c.ceipalProfileUrl || c.linkedinUrl}
//                                       target="_blank"
//                                       rel="noreferrer"
//                                       className="flex items-center gap-1.5 text-[11px] border rounded-lg px-2.5 py-1 font-medium text-[#012760] border-[#012760] hover:bg-[#012760] hover:text-white transition-all w-fit"
//                                     >
//                                       <ExternalLink className="w-3 h-3" /> View
//                                     </a>
//                                   ) : (
//                                     <span className="text-[11px] text-gray-300">—</span>
//                                   )}
//                                 </td>
//                               </tr>
//                             ))}
//                           </tbody>
//                         </table>
//                       </div>
//                     </div>
//                   )}

//                   {totalCount > 0 && (
//                     <Pagination
//                       currentPage={currentPage}
//                       totalPages={totalPages}
//                       onPageChange={handlePageChange}
//                       pageSize={pageSize}
//                       totalItems={totalCount}
//                       onPageSizeChange={handlePageSizeChange}
//                     />
//                   )}
//                 </>
//               )}
//             </div>
//           )}

//           {/* ══════════════════════ TAB: Analytics ══════════════════════ */}
//           {activeTab === "analytics" && (
//             <div>
//               {analyticsLoading ? (
//                 <div className="flex items-center justify-center h-48 text-gray-400 gap-2">
//                   <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
//                 </div>
//               ) : analyticsError ? (
//                 <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-3">
//                   <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
//                   <p className="text-sm text-red-700">{analyticsError}</p>
//                 </div>
//               ) : (
//                 <>
//                   <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
//                     {[
//                       { label: "Total in Ceipal", value: analytics?.totalCount ?? "—", icon: Database, color: "text-[#012760]", bg: "bg-[#f0f4ff]" },
//                       { label: "Avg Experience", value: analytics?.avgExperience ? `${analytics.avgExperience}y` : "—", icon: TrendingUp, color: "text-purple-700", bg: "bg-purple-50" },
//                       { label: "US Citizens / GC", value: analytics?.citizenCount ?? "—", icon: CheckCircle, color: "text-green-700", bg: "bg-green-50" },
//                       { label: "Need Sponsorship", value: analytics?.sponsorshipCount ?? "—", icon: AlertCircle, color: "text-orange-600", bg: "bg-orange-50" },
//                     ].map(({ label, value, icon: Icon, color, bg }) => (
//                       <div key={label} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
//                         <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center mb-3`}>
//                           <Icon className={`w-4 h-4 ${color}`} />
//                         </div>
//                         <p className="text-2xl font-bold text-gray-800 mb-0.5">{value}</p>
//                         <p className="text-xs text-gray-400">{label}</p>
//                       </div>
//                     ))}
//                   </div>

//                   <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
//                     <div className="flex items-center gap-2 mb-5">
//                       <TrendingUp className="w-4 h-4 text-[#012760]" />
//                       <p className="text-sm font-semibold text-[#012760]">Top Skills Across Ceipal</p>
//                     </div>
//                     {analytics?.topSkills?.length ? (
//                       <div className="space-y-4">
//                         {analytics.topSkills.map(({ skill, count }, i) => {
//                           const max = analytics.topSkills[0].count || 1;
//                           const colors = ["bg-[#012760]", "bg-blue-500", "bg-indigo-500", "bg-orange-400", "bg-purple-500"];
//                           return (
//                             <div key={skill}>
//                               <div className="flex justify-between text-xs mb-1.5">
//                                 <span className="text-gray-700 font-medium truncate max-w-[220px]">{skill}</span>
//                                 <span className="text-gray-400 ml-2 flex-shrink-0">{count.toLocaleString()} resumes</span>
//                               </div>
//                               <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
//                                 <div className={`h-full ${colors[i % colors.length]} rounded-full`} style={{ width: `${(count / max) * 100}%` }} />
//                               </div>
//                             </div>
//                           );
//                         })}
//                       </div>
//                     ) : (
//                       <p className="text-xs text-gray-400">
//                         Backend hasn't returned top-skills aggregation yet — see the `/ceipal/analytics`
//                         stub in ceipalRoutes.example.js.
//                       </p>
//                     )}
//                   </div>
//                 </>
//               )}
//             </div>
//           )}
//         </div>
//       </div>

//       <Footer />
//     </>
//   );
// };

// export default CeipalDBPage;












import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Search, X, CheckCircle, AlertCircle,
  Loader2, TrendingUp, RefreshCw, Download, ExternalLink,
  BarChart2, Database, RotateCcw,
} from "lucide-react";
import { USER_API_END_POINT } from "@/utils/constant";
import Footer from "../Footer";
import Pagination from "./Pagination";

const TABS = [
  { id: "search", label: "Search Hirees", icon: Search },
  { id: "analytics", label: "Analytics", icon: BarChart2 },
];

function mapCeipalRecordToCandidate(item, index) {
  const rawSkills = item.primary_skills || item.skills || "";
  const skills = Array.isArray(rawSkills)
    ? rawSkills
    : String(rawSkills).split(",").map((s) => s.trim()).filter(Boolean);

  return {
    id: item.applicant_id || item.id || `ceipal-${index}`,
    name: [item.first_name, item.middle_name, item.last_name].filter(Boolean).join(" ") || "Unknown",
    email: item.email_address || null,
    phone: item.mobile_number || item.work_phone_number || item.home_phone_number || null,
    jobTitle: item.job_title || "",
    experience: item.experience ? Number(item.experience) : null,
    skills,
    visaType: item.work_authorization || "Unspecified",
    location: [item.city, item.state, item.country].filter(Boolean).join(", "),
    linkedinUrl: item.linkedin_profile_url || null,
    ceipalProfileUrl: item.profile_url || null,
    updatedAt: item.resume_updated_on || item.modified_date || null,
  };
}

// Default filters — empty means "no filter applied", used both on first load
// and whenever the user hits Reset + Search again.
const EMPTY_FILTERS = {
  keywords: "",
  jobTitle: "",
  country: "",
  state: "",
  city: "",
};

const CeipalDBPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("search");

  const [keywords, setKeywords] = useState("");
  const [jobTitleInput, setJobTitleInput] = useState("");
  const [country, setCountry] = useState("");
  const [stateInput, setStateInput] = useState("");
  const [city, setCity] = useState("");

  // IMPORTANT: appliedFilters now starts as EMPTY_FILTERS (not null) so that
  // the data-fetch effect below fires immediately on page load, pulling the
  // full (paginated) dataset with no filters applied.
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  const [candidates, setCandidates] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === candidates.length ? new Set() : new Set(candidates.map((c) => c.id))
    );
  };
  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);

  const fetchCandidates = useCallback(async (filters, page, size) => {
    setLoading(true);
    setError(null);
    try {
      // Build params with job_title first so it's always the leading/primary
      // filter sent to the backend, followed by the rest in order.
      const params = new URLSearchParams({
        page,
        pageSize: size,
        ...(filters.jobTitle ? { jobTitle: filters.jobTitle } : {}),
        ...(filters.keywords ? { keywords: filters.keywords } : {}),
        ...(filters.country ? { country: filters.country } : {}),
        ...(filters.state ? { state: filters.state } : {}),
        ...(filters.city ? { city: filters.city } : {}),
      });

      const res = await fetch(`${USER_API_END_POINT}/ceipal/candidates?${params}`, {
        credentials: "include",
      });
      const data = await res.json();

      if (!data.success) throw new Error(data.message || "Failed to load Ceipal candidates");

      const list = Array.isArray(data.results) ? data.results : data.data || [];
      setCandidates(list.map(mapCeipalRecordToCandidate));
      setTotalCount(data.total ?? list.length);
    } catch (err) {
      console.error("Error fetching Ceipal candidates:", err);
      setError(err.message || "Failed to load Ceipal candidates");
    } finally {
      setLoading(false);
    }
  }, []);

  // Runs on mount (appliedFilters starts populated) AND whenever filters,
  // page, or page size change afterwards.
  useEffect(() => {
    fetchCandidates(appliedFilters, currentPage, pageSize);
  }, [appliedFilters, currentPage, pageSize, fetchCandidates]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [candidates]);

  useEffect(() => {
    if (activeTab !== "analytics") return;
    const fetchAnalytics = async () => {
      setAnalyticsLoading(true);
      setAnalyticsError(null);
      try {
        const res = await fetch(`${USER_API_END_POINT}/ceipal/analytics`, { credentials: "include" });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || "Failed to load analytics");
        setAnalytics(data.analytics);
      } catch (err) {
        setAnalyticsError(err.message || "Failed to load analytics");
      } finally {
        setAnalyticsLoading(false);
      }
    };
    fetchAnalytics();
  }, [activeTab]);

  const handleSearch = () => {
    setCurrentPage(1);
    setAppliedFilters({
      // job_title is the primary filter — put first for clarity, though
      // object key order doesn't affect behavior, fetchCandidates handles priority.
      jobTitle: jobTitleInput.trim(),
      keywords: keywords.trim(),
      country: country.trim(),
      state: stateInput.trim(),
      city: city.trim(),
    });
  };

  const handleReset = () => {
    setKeywords("");
    setJobTitleInput("");
    setCountry("");
    setStateInput("");
    setCity("");
    setCurrentPage(1);
    // Reset also re-triggers a full, unfiltered load immediately.
    setAppliedFilters(EMPTY_FILTERS);
  };

  const handleRefresh = () => {
    fetchCandidates(appliedFilters, currentPage, pageSize);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (size) => {
    setPageSize(parseInt(size, 10));
    setCurrentPage(1);
  };

  const inputCls =
    "w-full text-sm text-gray-700 placeholder-gray-400 bg-transparent outline-none";
  const fieldWrapCls =
    "flex flex-col gap-1 bg-white rounded-xl px-3 py-2 shadow-sm border border-white/10";
  const labelCls = "text-[9px] font-bold uppercase tracking-widest text-gray-400";

  return (
    <>
      <div className="min-h-screen bg-[#f8f9fc]">
        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <div className="relative w-full overflow-hidden">
          <div className="absolute inset-0 bg-[#012760]" />
          <div
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
          <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-400 rounded-full opacity-10 blur-3xl" />
          <div className="absolute -bottom-10 right-20 w-80 h-80 bg-blue-300 rounded-full opacity-10 blur-3xl" />
          <div className="relative w-full px-4 md:px-8 lg:px-10 py-10 md:py-12">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/50 hover:text-white text-xs font-medium mb-5 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Candidates
            </button>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-blue-300">Hirees</span>
                  <span className="w-1 h-1 rounded-full bg-blue-400 opacity-60" />
                  <span className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Applicant Database</span>
                </div>
                <h1 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-2">
                  Hirees <span className="text-blue-300">Candidate</span> Search
                </h1>
                <p className="text-white/50 text-sm max-w-xl">
                  Search across your full Hirees applicant database in real time — results are fetched
                  page by page, so this stays fast even with 300,000+ resumes.
                </p>
              </div>
              <div className="flex items-center gap-2 bg-white/10 border border-white/10 rounded-xl px-4 py-2">
                <Database className="w-3.5 h-3.5 text-white/60" />
                <span className="text-white text-xs font-semibold">
                  {totalCount ? totalCount.toLocaleString() : "—"} found
                </span>
              </div>
            </div>

            {/* ── Hirees-style search fields (main fields only) ── */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div className={fieldWrapCls}>
                  <label className={labelCls}>Keywords</label>
                  <input
                    type="text"
                    placeholder="e.g. React, JavaScript"
                    className={inputCls}
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>
                <div className={fieldWrapCls}>
                  <label className={labelCls}>Job Title</label>
                  <input
                    type="text"
                    placeholder='e.g. "Java Developer"'
                    className={inputCls}
                    value={jobTitleInput}
                    onChange={(e) => setJobTitleInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className={`${fieldWrapCls} flex-1 min-w-[160px]`}>
                  <label className={labelCls}>Country</label>
                  <input
                    type="text"
                    placeholder="United States"
                    className={inputCls}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>
                <div className={`${fieldWrapCls} flex-1 min-w-[160px]`}>
                  <label className={labelCls}>State</label>
                  <input
                    type="text"
                    placeholder="New Jersey"
                    className={inputCls}
                    value={stateInput}
                    onChange={(e) => setStateInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>
                <div className={`${fieldWrapCls} flex-1 min-w-[160px]`}>
                  <label className={labelCls}>City</label>
                  <input
                    type="text"
                    placeholder="Jersey City"
                    className={inputCls}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
                >
                  <Search className="w-4 h-4" />
                  Search
                </button>
                <button
                  onClick={handleReset}
                  className="flex items-center gap-2 px-4 py-3 border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-sm font-medium rounded-xl transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Tab bar ───────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
          <div className="w-full px-4 md:px-8 lg:px-10 flex gap-0">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap
                  ${activeTab === id
                    ? "border-[#012760] text-[#012760]"
                    : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200"
                  }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="w-full px-4 md:px-8 lg:px-10 py-8">
          {/* ══════════════════════ TAB: Search ══════════════════════ */}
          {activeTab === "search" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-gray-700">
                  {loading ? (
                    <span className="flex items-center gap-2 text-gray-400">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                    </span>
                  ) : (
                    <>
                      {totalCount.toLocaleString()}{" "}
                      <span className="font-normal text-gray-400">
                        {totalCount === 1 ? "candidate" : "candidates"} found
                      </span>
                    </>
                  )}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleRefresh}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Refresh
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all">
                    <Download className="w-3.5 h-3.5" />
                    Export
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-4 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                  <button
                    onClick={handleRefresh}
                    className="ml-auto text-xs font-medium text-red-600 hover:text-red-800 underline"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!loading && candidates.length === 0 && !error ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-14 text-center shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <X className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-gray-700 font-semibold mb-1">No candidates found</p>
                  <p className="text-gray-400 text-sm">Try different keywords or clearing a field</p>
                </div>
              ) : (
                <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                  <div
                    className="overflow-auto"
                    style={{ maxHeight: "calc(100vh - 320px)" }}
                  >
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-[#eef1fb] border-b border-[#012760]/10">
                          <th className="w-10 px-3 py-3">
                            <input
                              type="checkbox"
                              checked={candidates.length > 0 && selectedIds.size === candidates.length}
                              onChange={toggleSelectAll}
                              className="rounded border-gray-300 accent-[#012760]"
                            />
                          </th>
                          {[
                            "NAME", "EMAIL ID", "CONTACT NUMBER", "JOB TITLE", "EXPERIENCE",
                            "CURRENT EMPLOYER", "LOCATION", "WORK AUTHORIZATION",
                            "NOTICE PERIOD", "RESUME UPDATED ON", "",
                          ].map((label) => (
                            <th
                              key={label}
                              className="px-3 py-3 text-left text-[11px] font-semibold text-[#012760] tracking-wide whitespace-nowrap"
                            >
                              {label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {candidates.map((c) => (
                          <tr
                            key={c.id}
                            className="border-b border-gray-50 hover:bg-[#012760]/5 transition-colors"
                          >
                            <td className="px-3 py-2.5">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(c.id)}
                                onChange={() => toggleRow(c.id)}
                                className="rounded border-gray-300 accent-[#012760]"
                              />
                            </td>
                            <td className="px-3 py-2.5 text-[#012760] font-medium whitespace-nowrap">{c.name}</td>
                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.email || "N/A"}</td>
                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.phone || "N/A"}</td>
                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap max-w-[160px] truncate" title={c.jobTitle}>
                              {c.jobTitle || "N/A"}
                            </td>
                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                              {c.experience != null ? `${c.experience} yrs` : "N/A"}
                            </td>
                            <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">N/A</td>
                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap max-w-[180px] truncate" title={c.location}>
                              {c.location || "N/A"}
                            </td>
                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.visaType}</td>
                            <td className="px-3 py-2.5 text-gray-400 whitespace-nowrap">N/A</td>
                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{c.updatedAt || "N/A"}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              {c.ceipalProfileUrl || c.linkedinUrl ? (
                                <a
                                  href={c.ceipalProfileUrl || c.linkedinUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center gap-1.5 text-[11px] border rounded-lg px-2.5 py-1 font-medium text-[#012760] border-[#012760] hover:bg-[#012760] hover:text-white transition-all w-fit"
                                >
                                  <ExternalLink className="w-3 h-3" /> View
                                </a>
                              ) : (
                                <span className="text-[11px] text-gray-300">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {totalCount > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  pageSize={pageSize}
                  totalItems={totalCount}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
            </div>
          )}

          {/* ══════════════════════ TAB: Analytics ══════════════════════ */}
          {activeTab === "analytics" && (
            <div>
              {analyticsLoading ? (
                <div className="flex items-center justify-center h-48 text-gray-400 gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
                </div>
              ) : analyticsError ? (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{analyticsError}</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {[
                      { label: "Total in Ceipal", value: analytics?.totalCount ?? "—", icon: Database, color: "text-[#012760]", bg: "bg-[#f0f4ff]" },
                      { label: "Avg Experience", value: analytics?.avgExperience ? `${analytics.avgExperience}y` : "—", icon: TrendingUp, color: "text-purple-700", bg: "bg-purple-50" },
                      { label: "US Citizens / GC", value: analytics?.citizenCount ?? "—", icon: CheckCircle, color: "text-green-700", bg: "bg-green-50" },
                      { label: "Need Sponsorship", value: analytics?.sponsorshipCount ?? "—", icon: AlertCircle, color: "text-orange-600", bg: "bg-orange-50" },
                    ].map(({ label, value, icon: Icon, color, bg }) => (
                      <div key={label} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                        <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center mb-3`}>
                          <Icon className={`w-4 h-4 ${color}`} />
                        </div>
                        <p className="text-2xl font-bold text-gray-800 mb-0.5">{value}</p>
                        <p className="text-xs text-gray-400">{label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-5">
                      <TrendingUp className="w-4 h-4 text-[#012760]" />
                      <p className="text-sm font-semibold text-[#012760]">Top Skills Across Ceipal</p>
                    </div>
                    {analytics?.topSkills?.length ? (
                      <div className="space-y-4">
                        {analytics.topSkills.map(({ skill, count }, i) => {
                          const max = analytics.topSkills[0].count || 1;
                          const colors = ["bg-[#012760]", "bg-blue-500", "bg-indigo-500", "bg-orange-400", "bg-purple-500"];
                          return (
                            <div key={skill}>
                              <div className="flex justify-between text-xs mb-1.5">
                                <span className="text-gray-700 font-medium truncate max-w-[220px]">{skill}</span>
                                <span className="text-gray-400 ml-2 flex-shrink-0">{count.toLocaleString()} resumes</span>
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className={`h-full ${colors[i % colors.length]} rounded-full`} style={{ width: `${(count / max) * 100}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">
                        Backend hasn't returned top-skills aggregation yet — see the `/ceipal/analytics`
                        stub in ceipalRoutes.example.js.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default CeipalDBPage;